// The notes linked to a client, a project or a task (decision 90 v2, Carla 2026-10-12): shown on the
// contact record, in the project and in the task drawer, each with "+ New note" that opens Notes'
// chooser with the link pre-filled. A note opens in the Notes editor view (the deep link 'note').
// A failed load says so, with the server's words and Try again; it is never "no notes".
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { formatDateTime } from '../../utils/formatting';
import { Button, ErrorState, Skeleton } from '../../ui';
import { noteTitle, notePreview, editedAt } from './noteContent';
import './notesBrowse.css';

const QUERY = { contact: 'contactId', project: 'projectId', task: 'taskId' };

// Go to Notes through the app's own navigation (App.js applyNavigate): a page and one ?<entity>=<id>.
export function openNoteInNotes(id) {
  window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: `/notes?note=${encodeURIComponent(id)}` }));
}
export function newNoteFor(kind, id) {
  window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: `/notes?note_new=${encodeURIComponent(`${kind}:${id}`)}` }));
}

// kind: 'contact' | 'project' | 'task'; id: the record's id; title: the heading.
// limit: show only the newest few (the contact pop-up shows 3), with onViewAll as a "View all notes"
// link beside "+ New note" (Carla, 2026-10-21: the pop-up is a summary, the full page has them all).
export default function LinkedNotes({ kind, id, title = 'Notes', emptyText = 'No notes linked here yet.', limit = null, onViewAll = null }) {
  const [state, setState] = useState({ status: 'loading', notes: [], error: null });
  const load = useCallback(() => {
    if (id === null || id === undefined || id === '') return undefined;
    let alive = true;
    setState((s) => ({ ...s, status: 'loading', error: null }));
    api(`/api/notes?${QUERY[kind]}=${encodeURIComponent(id)}`)
      .then((r) => r.json())
      .then((data) => {
        if (!alive) return;
        if (!Array.isArray(data?.notes)) throw new Error('The notes linked here did not load: Aidey sent back something it could not read.');
        setState({ status: 'ready', notes: data.notes, error: null });
      })
      .catch((e) => { if (alive) setState({ status: 'failed', notes: [], error: messageFromError(e, 'The notes linked here did not load.') }); });
    return () => { alive = false; };
  }, [kind, id]);
  useEffect(() => load(), [load]);

  return (
    <section className="nt-linkednotes" aria-label={title} data-testid={`linked-notes-${kind}`}>
      <div className="nt-linkednotes__head">
        <h3 className="nt-linkednotes__title">{title}</h3>
        <span className="nt-linkednotes__actions">
          {onViewAll && state.status === 'ready' && state.notes.length > 0 && (
            <button type="button" className="nt-linkednotes__all" onClick={onViewAll}>
              View all notes{limit && state.notes.length > limit ? ` (${state.notes.length})` : ''}
            </button>
          )}
          <Button size="sm" variant="secondary" onClick={() => newNoteFor(kind, id)}>+ New note</Button>
        </span>
      </div>
      {state.status === 'failed' ? (
        <ErrorState title="Notes didn't load" message={state.error} onRetry={load} />
      ) : state.status === 'loading' ? (
        <Skeleton lines={1} label="Loading notes" />
      ) : state.notes.length === 0 ? (
        <p className="nt-linkednotes__empty">{emptyText}</p>
      ) : (
        <ul className="nt-linkednotes__list">
          {(limit ? state.notes.slice(0, limit) : state.notes).map((n) => {
            const when = editedAt(n);
            const preview = notePreview(n, 120);
            return (
              <li key={n.id}>
                <button type="button" className="nt-linkednotes__row" onClick={() => openNoteInNotes(n.id)}>
                  <span className="nt-linkednotes__name">{n.emoji ? `${n.emoji} ` : ''}{noteTitle(n)}{n.fromAidey ? ' · From Aidey' : ''}</span>
                  <span className="nt-linkednotes__meta">{[when ? formatDateTime(when, undefined, { year: undefined }) : null, preview].filter(Boolean).join(' · ')}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
